<script setup lang="ts">
import { computed, ref } from 'vue';
import dayjs, { type Dayjs } from 'dayjs';
import HoverCard from '@/components/HoverCard/HoverCard.vue';
import AvailabilityEntryDetails from './AvailabilityEntryDetails.vue';
import { getAvailabilityKind, type AvailabilityEntry } from '@/types/availability';
import { getAvailabilityCoverage, getAvailabilityRestrictions } from '@/util/availabilityDisplay';
import './availability.css';

interface MatchRecord extends Record<string, unknown> {
	group?: {
		groupNumber?: number | null;
		shortName?: string | null;
		name?: string | null;
	} | null;
	visibility?: string | null;
	matchTime?: string | null;
	matchEndTime?: string | null;
}

interface GroupedMatchEntry {
	groupNumber: number;
	groupName: string;
	matches: MatchRecord[];
	hasHidden: boolean;
	earliestTime: Dayjs | null;
	latestEndTime: Dayjs | null;
	matchCount: number;
	timeRange: string;
}

const hoverCardRef = ref<{ close?: () => void } | null>(null);

const props = withDefaults(
	defineProps<{
		date: Date | string;
		isToday?: boolean;
		isCurrentMonth?: boolean;
		isSelected?: boolean;
		isDragOver?: boolean;
		isWeekend?: boolean;
		availabilityEntries?: AvailabilityEntry[];
		blockEntries?: AvailabilityEntry[];
		matches?: MatchRecord[];
		matchCount?: number;
		mobileMode?: boolean;
	}>(),
	{
		isToday: false,
		isCurrentMonth: true,
		isSelected: false,
		isDragOver: false,
		isWeekend: false,
		availabilityEntries: () => [],
		blockEntries: () => [],
		matches: () => [],
		matchCount: 0,
		mobileMode: false
	}
);

const emit = defineEmits<{
	click: [{ date: string; event: MouseEvent }];
	dragStart: [{ date: string; event: MouseEvent }];
	dragEnter: [{ date: string; event: MouseEvent }];
	dragEnd: [{ date: string; event: MouseEvent }];
	setAvailability: [{ date: string }];
}>();

// Computed
const dateObj = computed(() => dayjs(props.date));
const dayNumber = computed(() => dateObj.value.date());
const dateStr = computed(() => dateObj.value.format('YYYY-MM-DD'));
const formattedDate = computed(() => dateObj.value.format('dddd, MMMM D'));
const availabilityEntries = computed(() => props.availabilityEntries ?? []);
const blockEntries = computed(() => props.blockEntries ?? []);
const matches = computed(() => props.matches ?? []);
const matchCount = computed(() => props.matchCount ?? 0);
const mobileMode = computed(() => props.mobileMode ?? false);
const isDragOver = computed(() => props.isDragOver ?? false);

const hasAvailability = computed(() => availabilityEntries.value.length > 0);
const restrictions = computed(() => getAvailabilityRestrictions(blockEntries.value));
const coverage = computed(() => getAvailabilityCoverage(availabilityEntries.value));
const hasMatches = computed(() => matchCount.value > 0);
const hasMatchDerived = computed(() => blockEntries.value.some((b) => getAvailabilityKind(b.blockType) === 'match'));

const entryStatus = computed(() => {
	if (restrictions.value.fullyBlocked) return 'blocked';
	if (restrictions.value.blocks.length > 0) return 'partial-blocked';
	if (coverage.value.fullyAvailable) return 'available';
	if (coverage.value.available.length > 0) return 'partial-available';
	return 'none';
});
const availabilityStatus = computed(() => (hasMatchDerived.value ? 'match' : entryStatus.value));
const isPartiallyBlocked = computed(() => entryStatus.value === 'partial-blocked');
const isPartiallyAvailable = computed(() => entryStatus.value === 'partial-available');
const partialText = computed(() => {
	if (isPartiallyBlocked.value) return `Partially blocked · ${restrictions.value.labels.join(' · ')}`;
	if (isPartiallyAvailable.value) return `Partially available · ${coverage.value.labels.join(' · ')}`;
	return '';
});
const partialBadges = computed(() => {
	if (isPartiallyBlocked.value) {
		return [
			...(restrictions.value.timeRestricted ? [{ icon: 'clock', label: 'Time restricted', className: 'time-restriction' }] : []),
			...(restrictions.value.scopeRestricted ? [{ icon: 'users', label: restrictions.value.scopeLabel, className: 'group-restriction' }] : [])
		];
	}
	if (isPartiallyAvailable.value) {
		return [
			...(coverage.value.timeLimited ? [{ icon: 'clock', label: 'Available at specific times', className: 'time-availability' }] : []),
			...(coverage.value.scopeLimited ? [{ icon: 'users', label: coverage.value.scopeLabel, className: 'group-availability' }] : [])
		];
	}
	return [];
});
const partialBadgeClasses = computed(() => (isPartiallyBlocked.value ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200' : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'));

// Availability status text for hover card
const statusText = computed(() => {
	switch (availabilityStatus.value) {
		case 'blocked':
			return 'Blocked all day for all groups';
		case 'partial-blocked':
		case 'partial-available':
			return partialText.value;
		case 'match':
			return restrictions.value.fullyBlocked ? 'You have a match scheduled · Also blocked all day' : 'You have a match scheduled';
		case 'available':
			return 'Available all day for all groups';
		default:
			return hasMatches.value ? 'No availability set · Matches scheduled' : 'No availability set';
	}
});

/**
 * Group matches by group with time ranges.
 * For each group, compute: name, start-end time span, match count, and if any are HIDDEN.
 */
const groupedMatches = computed(() => {
	if (matches.value.length === 0) return [];

	const groupMap = new Map<number, Omit<GroupedMatchEntry, 'matchCount' | 'timeRange'>>();

	for (const match of matches.value) {
		const groupNumber = match.group?.groupNumber ?? 0;
		const groupName = match.group?.shortName || match.group?.name || 'Unknown Group';
		const visibility = match.visibility || 'PUBLIC';

		if (!groupMap.has(groupNumber)) {
			groupMap.set(groupNumber, {
				groupNumber,
				groupName,
				matches: [],
				hasHidden: false,
				earliestTime: null,
				latestEndTime: null
			});
		}

		const entry = groupMap.get(groupNumber);
		if (!entry) continue;
		entry.matches.push(match);

		// Track visibility
		if (visibility === 'HIDDEN') {
			entry.hasHidden = true;
		}

		// Track time span
		const matchTime = match.matchTime ? dayjs(`2000-01-01T${match.matchTime}`) : null;
		const matchEndTime = match.matchEndTime ? dayjs(`2000-01-01T${match.matchEndTime}`) : null;

		if (matchTime && (!entry.earliestTime || matchTime.isBefore(entry.earliestTime))) {
			entry.earliestTime = matchTime;
		}
		if (matchEndTime && (!entry.latestEndTime || matchEndTime.isAfter(entry.latestEndTime))) {
			entry.latestEndTime = matchEndTime;
		} else if (matchTime && !matchEndTime && (!entry.latestEndTime || matchTime.isAfter(entry.latestEndTime))) {
			// Fallback: use matchTime if no endTime
			entry.latestEndTime = matchTime.add(2, 'hour');
		}
	}

	// Convert to array and format
	return Array.from(groupMap.values())
		.map(
			(g): GroupedMatchEntry => ({
				...g,
				matchCount: g.matches.length,
				timeRange: formatTimeRange(g.earliestTime, g.latestEndTime)
			})
		)
		.sort((a, b) => {
			// Sort by earliest time
			if (a.earliestTime && b.earliestTime) {
				return a.earliestTime.isBefore(b.earliestTime) ? -1 : 1;
			}
			return 0;
		});
});

/**
 * Format a time range string from two dayjs objects.
 */
function formatTimeRange(start: Dayjs | null, end: Dayjs | null) {
	if (!start) return '';
	const startStr = start.format('h:mm A');
	if (!end || start.isSame(end)) return startStr;
	return `${startStr} – ${end.format('h:mm A')}`;
}

// Tailwind classes for day cell - computed based on state
const cellClasses = computed(() => {
	const base = [
		'relative w-full h-full min-h-[50px] box-border',
		'py-1 px-1.5 rounded-md cursor-pointer select-none',
		'border-l-[3px] border-t border-r border-b border-surface',
		'transition-colors duration-100',
		'hover:brightness-95 dark:hover:brightness-110'
	];

	const borderLeft = (() => {
		switch (availabilityStatus.value) {
			case 'available':
			case 'partial-available':
				return 'border-l-green-500';
			case 'blocked':
			case 'partial-blocked':
				return 'border-l-red-500';
			case 'match':
				return 'border-l-blue-500';
			default:
				return props.isToday || props.isSelected || props.isDragOver ? 'border-l-primary' : 'border-l-transparent';
		}
	})();

	const background = (() => {
		if (availabilityStatus.value === 'available') return 'bg-green-500/10 dark:bg-green-500/20';
		if (availabilityStatus.value === 'blocked') return 'bg-red-500/10 dark:bg-red-500/20';
		if (availabilityStatus.value === 'partial-blocked') return 'availability-partial-red bg-red-500/10 dark:bg-red-500/20';
		if (availabilityStatus.value === 'partial-available') return 'availability-partial-green bg-green-500/10 dark:bg-green-500/20';
		if (availabilityStatus.value === 'match') return 'bg-blue-500/10 dark:bg-blue-500/20';
		if (props.isSelected || props.isDragOver) return 'bg-primary/20 dark:bg-primary/30';
		if (props.isToday) return 'bg-primary/5';
		if (props.isWeekend) return 'bg-primary/[0.03] hover:bg-primary/[0.07]';
		return 'bg-[var(--egs-content-background)]';
	})();

	const states = [];
	if (props.isSelected || props.isDragOver) states.push('ring-2 ring-inset ring-primary');
	if (!props.isCurrentMonth) states.push('opacity-40');
	if (mobileMode.value) states.push('aspect-square py-0.5 px-1');

	return [...base, borderLeft, background, ...states].join(' ');
});

// Day number classes
const numberClasses = computed(() => {
	if (props.isToday) {
		return 'inline-flex items-center justify-center w-[26px] h-[26px] rounded-full bg-primary text-white font-semibold text-sm';
	}
	return 'text-sm font-medium text-color leading-tight';
});

// Status badge classes for hover card
const statusClasses = computed(() => {
	const base = 'flex items-center gap-1.5 text-sm py-1.5 px-2 rounded-md';
	switch (availabilityStatus.value) {
		case 'available':
		case 'partial-available':
			return `${base} bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-200`;
		case 'blocked':
		case 'partial-blocked':
			return `${base} bg-red-500/10 text-red-700 dark:bg-red-500/20 dark:text-red-200`;
		case 'match':
			return `${base} bg-blue-500/10 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200`;
		default:
			return `${base} bg-[var(--egs-content-hover)] text-color-secondary`;
	}
});

// Event handlers
function handleClick(e: MouseEvent) {
	// Close hover card immediately when clicking to open the side panel
	if (hoverCardRef.value?.close) {
		hoverCardRef.value.close();
	}
	emit('click', { date: dateStr.value, event: e });
}

function handleMouseDown(e: MouseEvent) {
	if (e.button === 0) {
		// Left click only
		emit('dragStart', { date: dateStr.value, event: e });
	}
}

function handleMouseEnter(e: MouseEvent) {
	emit('dragEnter', { date: dateStr.value, event: e });
}

function handleMouseUp(e: MouseEvent) {
	emit('dragEnd', { date: dateStr.value, event: e });
}

// Handler for "Set availability" CTA in hover card
function handleSetAvailabilityClick() {
	// Close hover card
	if (hoverCardRef.value?.close) {
		hoverCardRef.value.close();
	}
	// Emit specialized event to open the panel for this specific date
	emit('setAvailability', { date: dateStr.value });
}
</script>

<template>
	<HoverCard ref="hoverCardRef" :placement="mobileMode ? 'bottom' : 'right'" :openDelayMs="400" :closeDelayMs="50" :disabled="isDragOver" triggerDisplay="flex">
		<template #trigger>
			<div :class="cellClasses" :id="`availability-day-${dateStr}`" @click="handleClick" @mousedown="handleMouseDown" @mouseenter="handleMouseEnter" @mouseup="handleMouseUp">
				<!-- Day number -->
				<div :class="numberClasses">
					{{ dayNumber }}
				</div>
				<span class="sr-only">{{ formattedDate }}. {{ statusText }}.</span>

				<!-- Match activity is independent of availability and assignments. -->
				<span v-if="hasMatches" class="match-day-dot absolute top-1.5 right-1.5 size-2.5 rounded-full bg-orange-500">
					<span class="sr-only">{{ matchCount }} match{{ matchCount === 1 ? '' : 'es' }} scheduled on this date</span>
				</span>

				<!-- Blocks take precedence; partial badges also remain visible on blue match dates. -->
				<div v-if="partialBadges.length" class="absolute bottom-1 left-1 right-1 flex items-center gap-0.5" :class="isPartiallyBlocked ? 'restriction-badges' : 'availability-badges'">
					<span v-for="badge in partialBadges" :key="badge.icon" class="inline-flex size-4 shrink-0 items-center justify-center rounded" :class="[partialBadgeClasses, badge.className]">
						<font-awesome-icon :icon="['fas', badge.icon]" class="text-xs" aria-hidden="true" />
						<span class="sr-only">{{ badge.label }}</span>
					</span>
				</div>
				<span v-else-if="restrictions.fullyBlocked && hasMatchDerived" class="absolute bottom-1 left-1 inline-flex size-4 items-center justify-center rounded bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200">
					<font-awesome-icon :icon="['fas', 'ban']" class="text-xs" aria-hidden="true" />
				</span>
			</div>
		</template>

		<template #content>
			<div class="min-w-[260px]">
				<!-- Header -->
				<div class="mb-2">
					<span class="font-semibold text-base text-color">{{ formattedDate }}</span>
				</div>

				<div class="h-px bg-surface my-2.5"></div>

				<!-- Matches section - grouped by group with time ranges -->
				<div v-if="matchCount > 0" class="mb-2">
					<div class="flex items-center gap-1.5 font-medium text-color mb-1.5">
						<font-awesome-icon :icon="['fas', 'futbol']" class="text-sm" />
						<span>{{ matchCount }} match{{ matchCount !== 1 ? 'es' : '' }} scheduled</span>
					</div>
					<div class="pl-[22px]">
						<div v-for="group in groupedMatches" :key="group.groupNumber ?? group.groupName" class="py-1.5 border-b border-surface last:border-b-0">
							<div class="flex items-center gap-1.5">
								<span class="text-[0.8125rem] font-medium text-color">{{ group.groupName }}</span>
								<span class="text-xs text-color-secondary">({{ group.matchCount }})</span>
								<span v-if="group.hasHidden" class="inline-flex items-center px-1.5 py-0.5 text-[0.65rem] text-orange-600 bg-orange-500/[0.12] rounded">
									<font-awesome-icon :icon="['fas', 'eye-slash']" />
								</span>
							</div>
							<div class="text-xs text-color-secondary mt-0.5">{{ group.timeRange }}</div>
						</div>
					</div>
				</div>

				<!-- Availability status -->
				<div class="mb-2">
					<div v-if="matches.length > 0" class="h-px bg-surface my-2.5"></div>
					<div :class="statusClasses">
						<font-awesome-icon v-if="availabilityStatus === 'available'" :icon="['fas', 'check']" class="text-sm" />
						<font-awesome-icon v-else-if="availabilityStatus === 'blocked'" :icon="['fas', 'ban']" class="text-sm" />
						<font-awesome-icon v-else-if="availabilityStatus === 'partial-blocked' || availabilityStatus === 'partial-available'" :icon="['fas', partialBadges[0]?.icon || 'clock']" class="text-sm" />
						<font-awesome-icon v-else-if="availabilityStatus === 'match'" :icon="['fas', 'calendar-check']" class="text-sm" />
						<font-awesome-icon v-else :icon="['far', 'circle']" class="text-sm" />
						<span>{{ statusText }}</span>
					</div>
				</div>

				<div v-if="availabilityStatus === 'match' && partialText" class="mb-2 rounded-md px-2 py-1.5 text-sm" :class="partialBadgeClasses">
					{{ partialText }}
				</div>
				<AvailabilityEntryDetails v-if="restrictions.blocks.length" :entries="restrictions.blocks" class="mb-2" />
				<AvailabilityEntryDetails v-if="coverage.available.length" :entries="coverage.available" kind="available" class="mb-2" />

				<!-- Click to set availability CTA -->
				<button
					class="flex items-center justify-center gap-1.5 w-full mt-2.5 py-2 px-3 border-none rounded-md bg-primary text-white text-[0.8125rem] font-medium cursor-pointer transition-colors hover:bg-primary-600"
					@click.stop="handleSetAvailabilityClick"
				>
					<font-awesome-icon :icon="['fas', hasAvailability ? 'edit' : 'calendar-plus']" class="text-xs" />
					<span>{{ hasAvailability ? 'Edit' : 'Set' }} availability</span>
				</button>
			</div>
		</template>
	</HoverCard>
</template>
